import { useEffect, useState, useCallback, useRef } from 'react'
import { leadService } from '../../leads/services/leads'
import type { WorkloadItem, ReallocationSuggestion, TelecallerWeight } from '../../../types'
import { UserRole } from '../../../types'
import { authService } from '../../identity/services/auth'
import {
  PhoneCall, TrendingUp, Clock, RefreshCw, BarChart3, CheckCircle,
  AlertTriangle, ArrowRight, Settings2, ChevronDown, ChevronUp,
} from 'lucide-react'

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—'
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return m > 0 ? `${m}m ${s}s` : `${s}s`
}

function priorityBand(score: number): 'HOT' | 'WARM' | 'COOL' {
  if (score >= 80) return 'HOT'
  if (score >= 50) return 'WARM'
  return 'COOL'
}

function bandBadgeClass(score: number) {
  const b = priorityBand(score)
  if (b === 'HOT') return 'badge-danger'
  if (b === 'WARM') return 'badge-warning'
  return 'badge-info'
}

function healthColor(h: string) {
  if (h === 'overloaded') return 'var(--danger-text)'
  if (h === 'underloaded') return 'var(--warning-text)'
  return 'var(--success-text)'
}

function healthLabel(h: string) {
  if (h === 'overloaded') return '● Overloaded'
  if (h === 'underloaded') return '● Underloaded'
  return '● Balanced'
}

// ── TC Card ──────────────────────────────────────────────────────────────────

function TCCard({ item }: { item: WorkloadItem }) {
  const isOverloaded = item.load_health === 'overloaded'

  return (
    <div
      className="card"
      style={isOverloaded ? { borderLeft: '3px solid var(--danger-text)' } : undefined}
    >
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-md font-semibold" style={{ color: 'var(--text-primary)' }}>
            {item.name}
          </h3>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
            {item.last_active
              ? `Last active ${new Date(item.last_active).toLocaleString()}`
              : 'Not active yet'}
          </p>
          <span className="text-xs font-medium mt-1 block" style={{ color: healthColor(item.load_health) }}>
            {healthLabel(item.load_health)}
          </span>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={item.open_queue_depth > 20 ? 'badge-danger' : item.open_queue_depth > 10 ? 'badge-warning' : 'badge-success'}>
            {item.open_queue_depth} open
          </span>
          {item.sla_breached_count > 0 && (
            <span className="badge-danger">
              {item.sla_breached_count} SLA breach{item.sla_breached_count > 1 ? 'es' : ''}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        {[
          { icon: PhoneCall,  value: item.total_calls,                          label: 'Calls',    color: 'var(--brand-600)' },
          { icon: TrendingUp, value: item.conversions,                           label: 'Conv.',    color: 'var(--success-text)' },
          { icon: Clock,      value: `${item.conversion_rate}%`,                 label: 'Rate',     color: 'var(--brand-600)' },
          { icon: PhoneCall,  value: formatDuration(item.avg_call_duration_seconds), label: 'Avg Call', color: 'var(--text-secondary)' },
        ].map(({ icon: Icon, value, label, color }) => (
          <div
            key={label}
            className="text-center p-2 rounded-md"
            style={{ background: 'var(--surface-subtle)' }}
          >
            <Icon className="h-3.5 w-3.5 mx-auto mb-1" style={{ color }} />
            <p className="text-sm font-bold tabular-nums" style={{ color: 'var(--text-primary)' }}>{value}</p>
            <p className="text-2xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>{label}</p>
          </div>
        ))}
      </div>

      <div className="flex items-center justify-between text-xs mb-1" style={{ color: 'var(--text-tertiary)' }}>
        <span>Queue utilisation</span>
        <span>{Math.min(100, Math.round((item.open_queue_depth / 25) * 100))}%</span>
      </div>
      <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--border-default)' }}>
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${Math.min(100, (item.open_queue_depth / 25) * 100)}%`,
            background: healthColor(item.load_health),
          }}
        />
      </div>

      <div className="flex items-center justify-between mt-3 pt-3" style={{ borderTop: '1px solid var(--border-default)' }}>
        <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Alloc. weight</span>
        <span className="text-xs font-semibold" style={{ color: 'var(--brand-600)' }}>
          {item.allocation_weight.toFixed(1)}
        </span>
      </div>
    </div>
  )
}

// ── Suggestion Row ───────────────────────────────────────────────────────────

function SuggestionRow({
  s,
  onAccept,
  onDismiss,
  accepting,
}: {
  s: ReallocationSuggestion
  onAccept: (s: ReallocationSuggestion) => void
  onDismiss: (leadId: number) => void
  accepting: boolean
}) {
  return (
    <div
      className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-lg"
      style={{ background: 'var(--surface-subtle)', border: '1px solid var(--border-default)' }}
    >
      {/* Lead info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <span className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>
            {s.vehicle_reg_no || `Lead #${s.lead_id}`}
          </span>
          {s.vehicle_model && (
            <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>· {s.vehicle_model}</span>
          )}
          <span className={bandBadgeClass(s.priority_score)}>
            {priorityBand(s.priority_score)}
          </span>
          <span
            className="text-xs px-1.5 py-0.5 rounded"
            style={{
              background: s.reason_code === 'SLA_BREACH' ? 'var(--danger-bg)' : 'var(--warning-bg)',
              color: s.reason_code === 'SLA_BREACH' ? 'var(--danger-text)' : 'var(--warning-text)',
            }}
          >
            {s.reason_code === 'SLA_BREACH' ? 'SLA' : 'Imbalance'}
          </span>
        </div>
        <p className="text-xs mb-2" style={{ color: 'var(--text-tertiary)' }}>{s.reason}</p>
        <div className="flex items-center gap-2 text-xs flex-wrap">
          <span style={{ color: 'var(--text-secondary)' }}>
            {s.current_tc_name}
            <span style={{ color: 'var(--text-tertiary)' }}> ({s.current_tc_queue_depth} open)</span>
          </span>
          <ArrowRight className="h-3 w-3 shrink-0" style={{ color: 'var(--text-tertiary)' }} />
          <span style={{ color: 'var(--success-text)', fontWeight: 500 }}>
            {s.suggested_tc_name}
            <span style={{ color: 'var(--text-tertiary)', fontWeight: 400 }}> ({s.suggested_tc_queue_depth} open)</span>
          </span>
          {s.hours_since_last_contact != null && (
            <span style={{ color: 'var(--text-tertiary)' }}>
              · last contact {s.hours_since_last_contact}h ago
            </span>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 shrink-0">
        <button
          className="btn-secondary text-xs py-1 px-3"
          onClick={() => onDismiss(s.lead_id)}
          disabled={accepting}
        >
          Dismiss
        </button>
        <button
          className="btn-primary text-xs py-1 px-3"
          onClick={() => onAccept(s)}
          disabled={accepting}
        >
          {accepting ? 'Moving…' : 'Accept'}
        </button>
      </div>
    </div>
  )
}

// ── Weight Editor ─────────────────────────────────────────────────────────────

function WeightEditor({ weights, onSave }: {
  weights: TelecallerWeight[]
  onSave: (userId: number, weight: number) => Promise<void>
}) {
  const [edits, setEdits] = useState<Record<number, string>>({})
  const [saving, setSaving] = useState<number | null>(null)
  const [saved, setSaved] = useState<number | null>(null)

  const totalWeight = weights.reduce((s, w) => {
    const raw = parseFloat(edits[w.user_id] ?? String(w.allocation_weight))
    return s + (isNaN(raw) ? w.allocation_weight : raw)
  }, 0)

  const handleSave = async (userId: number) => {
    const raw = parseFloat(edits[userId] ?? '')
    if (isNaN(raw) || raw <= 0) return
    setSaving(userId)
    try {
      await onSave(userId, raw)
      setSaved(userId)
      setTimeout(() => setSaved(null), 2000)
    } finally {
      setSaving(null)
    }
  }

  return (
    <div className="overflow-x-auto">
      <table className="data-table">
        <thead>
          <tr>
            <th>Telecaller</th>
            <th>Language</th>
            <th>Queue</th>
            <th>Weight</th>
            <th>Share</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {weights.map(w => {
            const raw = parseFloat(edits[w.user_id] ?? String(w.allocation_weight))
            const share = totalWeight > 0 ? ((isNaN(raw) ? w.allocation_weight : raw) / totalWeight * 100).toFixed(0) : '—'
            return (
              <tr key={w.user_id}>
                <td className="font-medium">{w.name}</td>
                <td>{w.language.toUpperCase()}</td>
                <td>{w.open_queue_depth}</td>
                <td>
                  <input
                    type="number"
                    min={0.1}
                    step={0.1}
                    className="input w-20 py-1 text-sm"
                    value={edits[w.user_id] ?? w.allocation_weight}
                    onChange={e => setEdits(prev => ({ ...prev, [w.user_id]: e.target.value }))}
                  />
                </td>
                <td>
                  <span className="text-sm font-semibold" style={{ color: 'var(--brand-600)' }}>{share}%</span>
                </td>
                <td>
                  {saved === w.user_id ? (
                    <CheckCircle className="h-4 w-4" style={{ color: 'var(--success-text)' }} />
                  ) : (
                    <button
                      className="btn-secondary text-xs py-1 px-2"
                      disabled={saving === w.user_id || edits[w.user_id] === undefined}
                      onClick={() => handleSave(w.user_id)}
                    >
                      {saving === w.user_id ? '…' : 'Save'}
                    </button>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function WorkloadPage() {
  const currentUser = authService.getCurrentUser()
  const canEditWeights = [UserRole.INSURANCE_HEAD, UserRole.DEALER_PRINCIPAL].includes(currentUser?.role as UserRole)

  const [items, setItems]                     = useState<WorkloadItem[]>([])
  const [suggestions, setSuggestions]         = useState<ReallocationSuggestion[]>([])
  const [summaryBreached, setSummaryBreached] = useState(0)
  const [summaryImbalanced, setSummaryImbalanced] = useState(0)
  const [weights, setWeights]                 = useState<TelecallerWeight[]>([])
  const [loading, setLoading]                 = useState(true)
  const [loadError, setLoadError]             = useState(false)
  const [accepting, setAccepting]             = useState<number | null>(null)
  const [acceptAllBusy, setAcceptAllBusy]     = useState(false)
  const [toast, setToast]                     = useState('')
  const [showWeights, setShowWeights]         = useState(false)
  const [dismissed, setDismissed]             = useState<Set<number>>(new Set())
  const intervalRef                           = useRef<ReturnType<typeof setInterval> | null>(null)

  const showToast = (msg: string) => {
    setToast(msg)
    setTimeout(() => setToast(''), 4000)
  }

  const load = useCallback(async () => {
    setLoadError(false)
    try {
      const [statsData, suggestionsData] = await Promise.all([
        leadService.getWorkloadStats(),
        leadService.getReallocationSuggestions(),
      ])
      setItems(statsData)
      setSuggestions(suggestionsData.suggestions)
      setSummaryBreached(suggestionsData.total_sla_breached)
      setSummaryImbalanced(suggestionsData.total_imbalanced)
    } catch {
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [])

  const loadWeights = useCallback(async () => {
    try {
      const data = await leadService.getTelecallerWeights()
      setWeights(data)
    } catch {
      // weight endpoint is IH/DP only; TL sees no error
    }
  }, [])

  useEffect(() => {
    load()
    if (canEditWeights) loadWeights()
    intervalRef.current = setInterval(load, 60_000)
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [load, loadWeights, canEditWeights])

  const handleAccept = async (s: ReallocationSuggestion) => {
    setAccepting(s.lead_id)
    try {
      await leadService.reassignLead(s.lead_id, s.suggested_tc_id)
      setSuggestions(prev => prev.filter(x => x.lead_id !== s.lead_id))
      showToast(`Lead reassigned to ${s.suggested_tc_name}`)
      await load()
    } catch {
      showToast('Failed to reassign — please try again')
    } finally {
      setAccepting(null)
    }
  }

  const handleDismiss = (leadId: number) => {
    setDismissed(prev => new Set(prev).add(leadId))
  }

  const handleAcceptAll = async () => {
    const visible = suggestions.filter(s => !dismissed.has(s.lead_id))
    if (!visible.length) return
    setAcceptAllBusy(true)
    let moved = 0
    for (const s of visible) {
      try {
        await leadService.reassignLead(s.lead_id, s.suggested_tc_id)
        moved++
      } catch { /* continue */ }
    }
    showToast(`${moved} lead${moved !== 1 ? 's' : ''} moved successfully`)
    setDismissed(new Set())
    await load()
    setAcceptAllBusy(false)
  }

  const handleSaveWeight = async (userId: number, weight: number) => {
    await leadService.setAllocationWeight(userId, weight)
    await loadWeights()
  }

  const visibleSuggestions = suggestions.filter(s => !dismissed.has(s.lead_id))
  const hasSuggestions = visibleSuggestions.length > 0

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="section-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <BarChart3 className="h-6 w-6" style={{ color: 'var(--brand-600)' }} />
            Workload Balancing
            <button
              className="btn-icon"
              onClick={() => { setLoading(true); load() }}
              disabled={loading}
              title="Refresh"
              aria-label="Refresh"
              style={{ marginLeft: 'auto' }}
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </h1>
          <p className="page-subtitle">Telecaller queue depth, performance and allocation</p>
        </div>
      </div>

      {toast && (
        <div className="alert-info">
          <CheckCircle className="h-4 w-4 shrink-0" />
          <span>{toast}</span>
        </div>
      )}

      <div className="alert-info text-xs items-start">
        <BarChart3 className="h-4 w-4 shrink-0 mt-0.5" />
        <span>
          <strong>Queue</strong> = each telecaller's currently open (not yet closed) leads — new
          leads are auto-assigned toward whoever has capacity, factoring in their{' '}
          <strong>allocation weight</strong> below. A telecaller sitting on a much bigger queue
          than teammates gets steered fewer new leads automatically, regardless of weight, until
          it balances out. <strong>Load health</strong> (Balanced/Underloaded/Overloaded) reflects
          that same queue-depth comparison at a glance.
        </span>
      </div>

      {/* TC cards */}
      {loading ? (
        <div className="empty-state">Loading…</div>
      ) : loadError ? (
        <div className="empty-state">
          <p className="font-medium" style={{ color: 'var(--text-primary)' }}>No telecallers found</p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary)' }}>
            Could not load workload data. Check your connection and try again.
          </p>
          <button
            className="btn-secondary mt-3"
            onClick={() => { setLoading(true); load() }}
          >
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="empty-state">No telecallers found</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {items.map(item => <TCCard key={item.user_id} item={item} />)}
        </div>
      )}

      {/* Reallocation suggestions */}
      {!loading && (
        <div className="card">
          {/* Section header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                Reallocation Suggestions
                {visibleSuggestions.length > 0 && (
                  <span
                    className="ml-2 text-xs font-bold px-1.5 py-0.5 rounded-full"
                    style={{ background: 'var(--brand-100)', color: 'var(--brand-600)' }}
                  >
                    {visibleSuggestions.length}
                  </span>
                )}
              </h2>
              {(summaryBreached > 0 || summaryImbalanced > 0) && (
                <div className="flex items-center gap-2 text-xs flex-wrap">
                  {summaryBreached > 0 && (
                    <span style={{ color: 'var(--danger-text)' }}>
                      <AlertTriangle className="h-3 w-3 inline mr-0.5" />
                      {summaryBreached} SLA breach{summaryBreached > 1 ? 'es' : ''}
                    </span>
                  )}
                  {summaryImbalanced > 0 && (
                    <span style={{ color: 'var(--warning-text)' }}>
                      · {summaryImbalanced} load imbalance{summaryImbalanced > 1 ? 's' : ''}
                    </span>
                  )}
                </div>
              )}
            </div>
            {hasSuggestions && (
              <button
                className="btn-primary text-xs py-1 px-3"
                onClick={handleAcceptAll}
                disabled={acceptAllBusy}
              >
                {acceptAllBusy ? 'Moving all…' : `Accept All (${visibleSuggestions.length})`}
              </button>
            )}
          </div>

          {/* Suggestion list */}
          {visibleSuggestions.length === 0 ? (
            <div className="empty-state py-6">
              <CheckCircle className="h-6 w-6 mx-auto mb-2" style={{ color: 'var(--success-text)' }} />
              <p>No reallocation needed right now.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {visibleSuggestions.map(s => (
                <SuggestionRow
                  key={s.lead_id}
                  s={s}
                  onAccept={handleAccept}
                  onDismiss={handleDismiss}
                  accepting={accepting === s.lead_id}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Allocation weights (IH / DP only) */}
      {canEditWeights && (
        <div className="card">
          <button
            className="flex items-center justify-between w-full text-left"
            onClick={() => { setShowWeights(v => !v); if (!showWeights && weights.length === 0) loadWeights() }}
          >
            <div className="flex items-center gap-2">
              <Settings2 className="h-4 w-4" style={{ color: 'var(--brand-600)' }} />
              <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                Allocation Weights
              </span>
              <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>
                — set per-telecaller probability share
              </span>
            </div>
            {showWeights
              ? <ChevronUp className="h-4 w-4" style={{ color: 'var(--text-tertiary)' }} />
              : <ChevronDown className="h-4 w-4" style={{ color: 'var(--text-tertiary)' }} />
            }
          </button>

          {showWeights && (
            <div className="mt-4">
              <p className="text-xs mb-3" style={{ color: 'var(--text-tertiary)' }}>
                Weight is a relative capacity multiplier, not a hard cap — a telecaller with
                weight <strong>2.0</strong> gets roughly twice as many new leads as one with{' '}
                <strong>1.0</strong>, all else equal. It's automatically softened by current queue
                depth (the "Queue" column), so an overloaded telecaller gets fewer new leads even
                at a high weight, until their queue comes back down. <strong>Share</strong> shows
                what each weight works out to as a percentage of new leads, given everyone's
                current weight — edit a number and watch it recompute live. Changes apply to
                future auto-allocations only, not leads already assigned.
              </p>
              {weights.length === 0 ? (
                <div className="empty-state py-4">No telecallers found</div>
              ) : (
                <WeightEditor weights={weights} onSave={handleSaveWeight} />
              )}
            </div>
          )}
        </div>
      )}

    </div>
  )
}
